/**
 * 音频播放器 - 简化版
 * 基于OpenAvatarChat的Player，简化为纯JS
 * VERSION: 2.2 - 共享 AudioContext + 可调语音增益 + 峰值限制
 */

console.log('🔧 Player.js 加载 - VERSION 2.2 (共享 AudioContext + 输出增益)');

// 🌍 共享 AudioContext：避免每新对话 new+close（Chrome 上限 6 个 AC，频繁切换会失败）
// 在首次用户手势（按麦克风）时调用 ensureSharedAudioContext().resume() 预热，
// 之后 Processor/Player 复用同一 ctx，规避自动播放策略带来的首段延迟。
let sharedAudioContext = null;
let sharedOutputGain = null;
let sharedOutputLimiter = null;
let digitalHumanOutputGain = 1.8;

const MIN_OUTPUT_GAIN = 1;
const MAX_OUTPUT_GAIN = 5;

const clampOutputGain = (value) => {
  const gain = Number(value);
  if (!Number.isFinite(gain)) return 1.8;
  return Math.min(MAX_OUTPUT_GAIN, Math.max(MIN_OUTPUT_GAIN, gain));
};

/* 所有数字人共用一条输出链，确保多 Player 切换时音量一致：
 * source → gain → limiter → destination。
 * 不直接乘 PCM，避免样本在写入 AudioBuffer 前被截断；限制器只压住接近满幅的峰值，
 * 低音量语音仍能完整获得增益。 */
function ensureSharedOutputChain(context) {
  if (!context) return null;
  if (sharedOutputGain) return sharedOutputGain;

  sharedOutputGain = context.createGain();
  sharedOutputGain.gain.value = digitalHumanOutputGain;

  try {
    sharedOutputLimiter = context.createDynamicsCompressor();
    sharedOutputLimiter.threshold.value = -3;
    sharedOutputLimiter.knee.value = 0;
    sharedOutputLimiter.ratio.value = 20;
    sharedOutputLimiter.attack.value = 0.003;
    sharedOutputLimiter.release.value = 0.18;
    sharedOutputGain.connect(sharedOutputLimiter);
    sharedOutputLimiter.connect(context.destination);
  } catch (error) {
    console.warn('⚠️ 数字人峰值限制器创建失败，回退到直接增益:', error);
    sharedOutputLimiter = null;
    sharedOutputGain.connect(context.destination);
  }
  return sharedOutputGain;
}

export function setDigitalHumanOutputGain(value) {
  digitalHumanOutputGain = clampOutputGain(value);
  if (sharedAudioContext && sharedOutputGain) {
    const now = sharedAudioContext.currentTime;
    const param = sharedOutputGain.gain;
    param.cancelScheduledValues(now);
    // 30ms 平滑变化，避免播放过程中拖动滑块产生突变噪声。
    param.setTargetAtTime(digitalHumanOutputGain, now, 0.015);
  }
  return digitalHumanOutputGain;
}

export function getDigitalHumanOutputGain() {
  return digitalHumanOutputGain;
}

export function ensureSharedAudioContext() {
  if (!sharedAudioContext) {
    try {
      sharedAudioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      console.warn('⚠️ 共享 AudioContext 创建失败:', e);
      return null;
    }
  }
  ensureSharedOutputChain(sharedAudioContext);
  // resume 必须在用户手势栈内调用才生效；外层 (mic mousedown) 调用即可
  if (sharedAudioContext.state === 'suspended') {
    sharedAudioContext.resume().catch(() => {});
  }
  return sharedAudioContext;
}
export function getSharedAudioContext() { return sharedAudioContext; }

export class Player {
  constructor(options) {
    console.log('🎵 创建 Player 实例');

    this.options = {
      channels: 1,
      sampleRate: 24000,
      ...options
    };

    this.audioContext = null;
    this.samplesList = [];
    this.startTime = 0;
    this.firstStartAbsoluteTime = null; // 首次播放的绝对时间戳
    // 已 schedule 到 AudioContext 时间线上的 BufferSourceNode
    // ⚠️ 必须记录，destroy 时遍历 stop()。
    //  source.start(t) 之后 source 脱离 Player 独立运行，
    //  不主动 stop 会一直播到 buffer 结束 → 与新 Player 的音频叠加（"两个数字人同时讲"）
    this.activeSources = [];
    this.destroyed = false;
    this.muted = false;
    this.muteGain = null;

    this.initAudioContext();
  }

  setMuted(muted) {
    this.muted = !!muted;
    if (this.muteGain && this.audioContext) {
      this.muteGain.gain.setValueAtTime(this.muted ? 0 : 1, this.audioContext.currentTime);
    }
  }

  initAudioContext() {
    // 优先复用共享 AudioContext（已被用户手势 resume 过）；否则兜底新建
    this.audioContext = sharedAudioContext
      || (sharedAudioContext = new (window.AudioContext || window.webkitAudioContext)());
    const out = ensureSharedOutputChain(this.audioContext) || this.audioContext.destination;
    this.muteGain = this.audioContext.createGain();
    this.muteGain.gain.value = this.muted ? 0 : 1;
    this.muteGain.connect(out);
    this.startTime = this.audioContext.currentTime;
  }
  
  /**
   * 喂入音频数据
   * @param {Object} audioData 
   * @param {Int16Array} audioData.audio - PCM Int16数据
   * @param {boolean} audioData.end_of_batch - 是否是最后一段
   */
  feed(audioData) {
    const { audio, end_of_batch } = audioData;
    
    if (!audio) return;

    // 静音只把增益置 0，仍按真实时长调度，口型才能跟 BlendShapes 走。
    // 旧逻辑直接 return 并在 80ms 内 onEndSpeaking，嘴几乎不动。
    
    // 转换Int16为Float32
    const float32 = new Float32Array(audio.length);
    for (let i = 0; i < audio.length; i++) {
      float32[i] = audio[i] / 32768.0;
    }
    
    const samples = {
      data: float32,
      end_of_batch,
      startTime: null
    };
    
    this.samplesList.push(samples);
    this.flush(samples, this.samplesList.length - 1);
  }
  
  flush(samples, index) {
    if (!samples || !this.audioContext || this.destroyed) return;
    
    const { data, end_of_batch } = samples;
    
    // 创建音频buffer
    const length = data.length / this.options.channels;
    const audioBuffer = this.audioContext.createBuffer(
      this.options.channels,
      length,
      this.options.sampleRate
    );
    
    // 填充数据（简单的淡入淡出）
    for (let channel = 0; channel < this.options.channels; channel++) {
      const audioData = audioBuffer.getChannelData(channel);
      let offset = channel;
      let decrement = 50;
      
      for (let i = 0; i < length; i++) {
        audioData[i] = data[offset];
        
        // 淡入（前50个样本）
        if (i < 50) {
          audioData[i] = (audioData[i] * i) / 50;
        }
        // 淡出（后50个样本）
        if (i >= length - 51) {
          audioData[i] = (audioData[i] * decrement--) / 50;
        }
        
        offset += this.options.channels;
      }
    }
    
    // 调整播放时间
    const currentTime = this.audioContext.currentTime;
    const oldStartTime = this.startTime;
    
    if (this.startTime < currentTime) {
      this.startTime = currentTime;
      console.log(`⏰ 调整播放时间: ${oldStartTime.toFixed(2)}s → ${this.startTime.toFixed(2)}s (当前时间=${currentTime.toFixed(2)}s)`);
    }
    
    // 创建音频源并播放
    const source = this.audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.muteGain || ensureSharedOutputChain(this.audioContext) || this.audioContext.destination);
    
    console.log(`🎵 调度播放段${index + 1}: startTime=${this.startTime.toFixed(2)}s, duration=${audioBuffer.duration.toFixed(2)}s, end_of_batch=${end_of_batch}`);
    
    source.start(this.startTime);
    // 记录到 activeSources，destroy 时统一 stop，避免打断后继续放
    this.activeSources.push(source);
    source.onended = source.onended || null; // 占位，下面真正赋值时再覆盖
    const _origCleanup = () => {
      const idx = this.activeSources.indexOf(source);
      if (idx >= 0) this.activeSources.splice(idx, 1);
    };

    // 记录时间戳
    samples.startTime = this.startTime;
    
    if (this.firstStartAbsoluteTime === null) {
      this.firstStartAbsoluteTime = Date.now();
      if (this.options.onStartSpeaking) {
        this.options.onStartSpeaking(this);
      }
    }
    
    this.startTime += audioBuffer.duration;
    console.log(`⏭️  下一段将在 ${this.startTime.toFixed(2)}s 播放`);
    
    // 播放结束回调
    if (end_of_batch && index === this.samplesList.length - 1) {
      source.onended = () => {
        _origCleanup();
        if (this.destroyed) return; // 被打断的 source onended，不再回调外层
        console.log(`🏁 段${index + 1}播放结束，触发 onEndSpeaking`);
        if (this.options.onEndSpeaking) {
          this.options.onEndSpeaking(this);
        }
      };
    } else {
      source.onended = _origCleanup;
    }
  }

  destroy() {
    this.destroyed = true;
    this.samplesList = [];
    // ★ 关键：把已 schedule 到 AudioContext 时间线上的 source 全部 stop
    //  否则 destroy 后这些 source 会继续播完，与新 Player 的音频叠加
    //  （表现：用户按按钮"打断"，旧角色还在讲、新角色又开口 → 两声叠加）
    for (const src of this.activeSources) {
      try {
        src.onended = null;       // 防止已结束/将停止的 onended 触发 onEndSpeaking
        src.stop(0);              // 立即停（参数 0/省略均表示"现在"）
        src.disconnect();
      } catch (_) { /* 已结束的 source.stop 会抛，忽略 */ }
    }
    this.activeSources = [];
    if (this.muteGain) {
      try {
        this.muteGain.disconnect();
      } catch (_) { /* ignore */ }
      this.muteGain = null;
    }
    // ⚠️ 不 close 共享 AudioContext（其他 Player 还要用）；只解绑引用
    this.audioContext = null;
  }
}

